import React, { useState, useEffect } from 'react'
import { Camera, Upload, CheckCircle, AlertCircle, Clock, Loader2 } from 'lucide-react'
import { PhotoTheme } from '../../types'
import { apiService } from '../../services/api'

interface PhotoThemesProps {
  onPhotoUploaded?: () => void
}

interface ThemeWithSubmission extends PhotoTheme {
  hasSubmission?: boolean
  submissionStatus?: 'none' | 'uploading' | 'uploaded' | 'error'
}

/**
 * Resolve the MIME type for a file. iOS Safari sometimes reports empty or
 * "application/octet-stream" for HEIC files, so fall back to extension-based detection.
 */
function resolveFileMimeType(file: File): string {
  const type = file.type?.toLowerCase()

  // If the browser reported a valid image MIME type, trust it
  if (type && type.startsWith('image/')) {
    return type
  }

  // Fallback: detect from file extension (common on iOS for HEIC)
  const extension = file.name.split('.').pop()?.toLowerCase()
  const extensionMimeMap: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'heic': 'image/heic',
    'heif': 'image/heif',
  }

  if (extension && extensionMimeMap[extension]) {
    return extensionMimeMap[extension]
  }

  // Return whatever the browser gave us (will fail validation if not an image)
  return type || 'application/octet-stream'
}

export const PhotoThemes: React.FC<PhotoThemesProps> = ({ onPhotoUploaded }) => {
  const [themes, setThemes] = useState<ThemeWithSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploadingTheme, setUploadingTheme] = useState<string | null>(null)
  const [captions, setCaptions] = useState<Record<string, string>>({})

  useEffect(() => {
    loadCurrentThemes()
  }, [])

  const loadCurrentThemes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.getCurrentThemes()
      
      if (response.error) {
        setError(response.error.message)
        return
      }

      if (response.data) {
        // The backend response might be wrapped
        const themesData = (response.data as any)?.themes || response.data || []
        setThemes(themesData.map((theme: PhotoTheme) => ({
          ...theme,
          hasSubmission: !!theme.userSubmission,
          submissionStatus: theme.userSubmission ? 'uploaded' : 'none'
        })))
      }
    } catch (err) {
      setError('Failed to load photo themes')
      console.error('Error loading themes:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (themeId: string, file: File) => {
    // Resolve MIME type — iOS sometimes reports empty or generic types for HEIC
    const resolvedType = resolveFileMimeType(file)

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']
    if (!allowedTypes.includes(resolvedType)) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'unknown'
      setError(`Unsupported file type ".${extension}". Please upload a JPG, PNG, or HEIC image.`)
      return
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
      setError(`File is too large (${sizeMB}MB). Maximum size is 10MB. Try reducing the image quality or resolution.`)
      return
    }

    try {
      setUploadingTheme(themeId)
      setError(null)
      
      // Update theme status to uploading
      setThemes(prev => prev.map(theme => 
        theme.themeId === themeId 
          ? { ...theme, submissionStatus: 'uploading' }
          : theme
      ))

      const response = await apiService.uploadPhoto(themeId, file, captions[themeId] || '')
      
      if (response.error) {
        setError(response.error.message)
        // Update theme status to error
        setThemes(prev => prev.map(theme => 
          theme.themeId === themeId 
            ? { ...theme, submissionStatus: 'error' }
            : theme
        ))
        return
      }

      // Update theme status to uploaded
      setThemes(prev => prev.map(theme => 
        theme.themeId === themeId 
          ? { ...theme, hasSubmission: true, submissionStatus: 'uploaded' }
          : theme
      ))

      // Clear caption state after successful upload
      setCaptions(prev => {
        const next = { ...prev }
        delete next[themeId]
        return next
      })

      // Notify parent component
      onPhotoUploaded?.()
      
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload photo'
      setError(`Upload failed: ${message}. Please try again.`)
      console.error('Upload error:', err)
      // Update theme status to error
      setThemes(prev => prev.map(theme => 
        theme.themeId === themeId 
          ? { ...theme, submissionStatus: 'error' }
          : theme
      ))
    } finally {
      setUploadingTheme(null)
    }
  }

  const handleFileSelect = (themeId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(themeId, file)
    }
  }

  const handleDrop = (themeId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (file) {
      handleFileUpload(themeId, file)
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const getStatusIcon = (theme: ThemeWithSubmission) => {
    switch (theme.submissionStatus) {
      case 'uploading':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />
      case 'uploaded':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <Camera className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusText = (theme: ThemeWithSubmission) => {
    switch (theme.submissionStatus) {
      case 'uploading':
        return 'Uploading...'
      case 'uploaded':
        return 'Photo submitted'
      case 'error':
        return 'Upload failed'
      default:
        return 'No photo submitted'
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error && themes.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Themes</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadCurrentThemes}
            className="bg-fwo-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Today's Photo Themes</h2>
        <div className="text-sm text-gray-500">
          {new Date().toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {themes.length === 0 ? (
        <div className="text-center py-8">
          <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Themes Available</h3>
          <p className="text-gray-600">
            No photo themes are available for today. Check back later!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {themes.map((theme) => (
            <div
              key={theme.themeId}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-1">{theme.title}</h3>
                  <p className="text-sm text-gray-600">{theme.description}</p>
                </div>
                <div className="flex items-center ml-4">
                  {getStatusIcon(theme)}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {getStatusText(theme)}
                </span>

                {theme.submissionStatus !== 'uploaded' && (
                  <div className="flex items-center space-x-2">
                    <div
                      className="relative"
                      onDrop={(e) => handleDrop(theme.themeId, e)}
                      onDragOver={handleDragOver}
                    >
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
                        onChange={(e) => handleFileSelect(theme.themeId, e)}
                        disabled={uploadingTheme === theme.themeId || (captions[theme.themeId]?.length ?? 0) > 200}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        id={`file-${theme.themeId}`}
                      />
                      <label
                        htmlFor={`file-${theme.themeId}`}
                        className={`
                          inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium
                          ${uploadingTheme === theme.themeId || (captions[theme.themeId]?.length ?? 0) > 200
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
                          }
                          transition-colors
                        `}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingTheme === theme.themeId ? 'Uploading...' : 'Upload Photo'}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Thumbnail display for submitted photos */}
              {theme.submissionStatus === 'uploaded' && theme.userSubmission && (
                <div className="mt-3 flex items-center gap-3 p-2 bg-green-50 rounded-lg border border-green-200">
                  {theme.userSubmission.thumbnailUrl ? (
                    <img
                      src={theme.userSubmission.thumbnailUrl}
                      alt={`Submission for ${theme.title}`}
                      className="w-16 h-16 object-cover rounded-md border border-green-300"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-md border border-gray-300">
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800">Photo submitted</p>
                    <p className="text-xs text-green-600">
                      {theme.userSubmission.thumbnailUrl
                        ? 'Your submission is ready'
                        : 'Processing thumbnail…'}
                    </p>
                  </div>
                </div>
              )}

              {theme.submissionStatus !== 'uploaded' && (
                <div className="mt-3">
                  <label htmlFor={`caption-${theme.themeId}`} className="block text-sm font-medium text-gray-700 mb-1">
                    Caption (optional) <span className="font-normal text-gray-400">— enter before uploading</span>
                  </label>
                  <input
                    id={`caption-${theme.themeId}`}
                    type="text"
                    value={captions[theme.themeId] || ''}
                    onChange={(e) => setCaptions(prev => ({ ...prev, [theme.themeId]: e.target.value }))}
                    placeholder="Add a caption for your photo..."
                    maxLength={250}
                    disabled={uploadingTheme === theme.themeId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <p className={`mt-1 text-xs text-right ${(captions[theme.themeId]?.length ?? 0) > 200 ? 'text-red-500' : 'text-gray-400'}`}>
                    {captions[theme.themeId]?.length ?? 0}/200
                  </p>
                </div>
              )}

              {theme.submissionStatus !== 'uploaded' && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-xs text-gray-500 text-center">
                    Drag and drop an image here, or click "Upload Photo" to select a file
                    <br />
                    Supported formats: JPG, PNG, HEIC (max 10MB)
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}